import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "zen-sound-id"

export default class extends Controller {
    static targets = ["menu", "trigger", "icon", "item"]
    static values = {
        sounds: { type: Array, default: [] }
    }

    connect() {
        this.audio = new Audio()
        this.audio.loop = true
        this.selectedId = localStorage.getItem(STORAGE_KEY)
        this.playing = false

        if (this.selectedId) {
            const sound = this.soundsValue.find((entry) => entry.id === this.selectedId)
            if (sound) {
                this.audio.src = sound.url
            } else {
                this.selectedId = null
                localStorage.removeItem(STORAGE_KEY)
            }
        }

        this.outsideClick = (event) => {
            if (!this.element.contains(event.target)) this.closeMenu()
        }
        this.keydown = (event) => {
            if (event.key === "Escape") this.closeMenu()
        }

        this.audio.addEventListener("play", () => {
            this.playing = true
            this.pingPlayed()
            this.syncUi()
        })
        this.audio.addEventListener("pause", () => {
            this.playing = false
            this.syncUi()
        })

        this.syncUi()
    }

    disconnect() {
        this.closeMenu()
        this.audio.pause()
        this.audio = null
    }

    toggleTrigger(event) {
        event.preventDefault()
        event.stopPropagation()

        if (this.menuTarget.classList.contains("is-open")) {
            this.closeMenu()
            return
        }

        if (this.playing) {
            this.pause()
            return
        }

        if (this.selectedId) {
            this.openMenu()
            return
        }

        this.openMenu()
    }

    openMenu() {
        this.menuTarget.classList.add("is-open")
        this.triggerTarget.setAttribute("aria-expanded", "true")
        document.addEventListener("click", this.outsideClick)
        document.addEventListener("keydown", this.keydown)
    }

    closeMenu() {
        if (this.hasMenuTarget) this.menuTarget.classList.remove("is-open")
        if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "false")
        document.removeEventListener("click", this.outsideClick)
        document.removeEventListener("keydown", this.keydown)
    }

    select(event) {
        event.preventDefault()
        const id = event.currentTarget.dataset.soundId
        const url = event.currentTarget.dataset.soundUrl

        if (id === this.selectedId && this.playing) {
            this.pause()
        } else if (id === this.selectedId) {
            this.play()
        } else {
            this.selectedId = id
            localStorage.setItem(STORAGE_KEY, id)
            this.audio.src = url
            this.play()
        }

        this.syncUi()
        this.closeMenu()
    }

    turnOff(event) {
        event.preventDefault()
        this.selectedId = null
        localStorage.removeItem(STORAGE_KEY)
        this.pause()
        this.audio.removeAttribute("src")
        this.syncUi()
        this.closeMenu()
    }

    play() {
        if (!this.audio.src) return

        this.audio.play().catch(() => {})
    }

    pingPlayed() {
        if (this.playedPinged) return
        this.playedPinged = true

        fetch("/zen/sound_played", {
            method: "POST",
            headers: {
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
            }
        }).catch(() => {})
    }

    pause() {
        this.audio.pause()
    }

    syncUi() {
        const playing = this.playing && !this.audio.paused

        this.triggerTarget.classList.toggle("is-playing", playing)
        this.triggerTarget.setAttribute("aria-label", playing ? "Pause ambient sounds" : "Ambient sounds")

        if (this.hasIconTarget) {
            const icon = this.iconTarget
            icon.classList.remove("bi-volume-up", "bi-volume-up-fill", "bi-pause-fill")
            if (playing) {
                icon.classList.add("bi-pause-fill")
            } else if (this.selectedId) {
                icon.classList.add("bi-volume-up-fill")
            } else {
                icon.classList.add("bi-volume-up")
            }
        }

        this.itemTargets.forEach((item) => {
            const active = item.dataset.soundId === this.selectedId
            item.classList.toggle("is-active", active)
            item.classList.toggle("is-playing", active && playing)
        })
    }
}
